/**
 * ChatBubble — Floating chat navigation bubble
 *
 * NEW component for BrainsWay Report.
 * Fixed position bottom-right (RTL-aware), unread count badge, navigates to /messages.
 * Neubrutalism styling with spring animation entrance.
 */

import { memo, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import { useTotalUnreadCount } from '../hooks';

export const ChatBubble = memo(function ChatBubble() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(MD_BREAKPOINT);
  const unreadCount = useTotalUnreadCount();
  const [isVisible, setIsVisible] = useState(false);

  // Don't show on messaging pages
  const isOnMessaging = location.pathname.startsWith('/messages');

  // Delay entrance for spring animation
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 500);
    return () => clearTimeout(timer);
  }, []);

  // On desktop, the StickyChatWidget replaces this FAB
  if (!isMobile) return null;

  if (isOnMessaging) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          type="button"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => navigate('/messages')}
          className="fixed bottom-20 end-4 z-50 flex h-14 w-14 items-center justify-center rounded-full border border-white/20 bg-slate-700 text-white shadow-sm transition-shadow hover:shadow-sm md:bottom-6 md:end-6"
          aria-label="Open messages"
        >
          {/* Chat icon */}
          <svg
            className="h-6 w-6"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          </svg>

          {/* Unread badge */}
          {unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -end-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full border border-white/20 bg-error-500 px-1 text-[10px] font-black text-white"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </motion.span>
          )}
        </motion.button>
      )}
    </AnimatePresence>
  );
});

export default ChatBubble;
